import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Avatar,
  Divider,
  Tab,
  Tabs,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Badge,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import EmailNotificationsSetting from '../../components/profile/EmailNotificationsSetting';
import { useNavigate } from 'react-router-dom';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { validatePassword } from '../../utils/passwordValidator';
import { validateCPFOrCNPJ, formatCPFOrCNPJ } from '../../utils/cpfCnpjValidator';
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter';
import ProfessionalSubscriptionPopup from '../../components/ProfessionalSubscriptionPopup';
import * as userService from '../../services/userService';
import * as subscriptionService from '../../services/subscriptionService';
import useFileUpload from '../../hooks/useFileUpload';
import logger from '../../utils/logger';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

interface AddressFormData {
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

const emptyAddress: AddressFormData = {
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
};

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const serializeAddress = (address: AddressFormData) => {
  const parts = [
    address.street.trim(),
    address.number.trim(),
    address.complement.trim(),
    address.neighborhood.trim(),
    address.city.trim(),
    address.state.trim() ? `UF ${address.state.trim()}` : '',
    address.cep.trim() ? `CEP ${address.cep.trim()}` : '',
  ].filter(Boolean);

  return parts.join(', ');
};

const parseAddress = (value?: string): AddressFormData => {
  if (!value) return emptyAddress;

  const parsed = { ...emptyAddress };
  const cepMatch = value.match(/CEP\s*(\d{5}-?\d{3})/i);
  const stateMatch = value.match(/UF\s*([A-Z]{2})/i);

  if (cepMatch) parsed.cep = formatCep(cepMatch[1]);
  if (stateMatch) parsed.state = stateMatch[1].toUpperCase();

  const cleaned = value
    .replace(/,\s*CEP\s*\d{5}-?\d{3}/i, '')
    .replace(/,\s*UF\s*[A-Z]{2}/i, '');

  const segments = cleaned
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (segments[0]) parsed.street = segments[0];
  if (segments[1]) parsed.number = segments[1];
  if (segments[2]) parsed.complement = segments[2];
  if (segments[3]) parsed.neighborhood = segments[3];
  if (segments[4]) parsed.city = segments[4];

  return parsed;
};

const ProfilePage: React.FC = () => {
  const { user, updateUser, uploadAvatar, deleteAvatar, refreshUserData, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    oab: '',
    estado_oab: '',
    cpf_cnpj: '',
    officeAddress: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [addressData, setAddressData] = useState<AddressFormData>(emptyAddress);
  const [isEditing, setIsEditing] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [subscription, setSubscription] = useState<subscriptionService.Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [cepLoading, setCepLoading] = useState(false);

  // Estados para o popup de planos principal
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);



  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);

  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [passwordWarnings, setPasswordWarnings] = useState<string[]>([]);
  const [passwordScore, setPasswordScore] = useState<number>(0);

  // Replace the existing file handling code with our new hook
  const {
    fileState,
    fileInputRef,
    handleFileChange,
    triggerFileInput,
    uploadFile
  } = useFileUpload({
    maxSizeInBytes: 2 * 1024 * 1024, // 2MB
    acceptedFileTypes: ['image/jpeg', 'image/png', 'image/jpg']
  });

  // Buscar dados atualizados do usuário ao montar o componente
  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      if (isMounted) {
        await refreshUserData();
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remover refreshUserData das dependências

  useEffect(() => {
    if (user) {
      console.log('🔍 [PROFILE] User data received:', user);
      console.log('🔍 [PROFILE] Mapping fields:', {
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        oab_number: user.oab_number,
        numero_oab: user.numero_oab,
        estado_oab: user.estado_oab,
        cpf_cnpj: user.cpf_cnpj
      });

      setFormData(prevData => ({
        ...prevData,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        oab: user.oab_number || '',
        estado_oab: user.estado_oab || '',
        cpf_cnpj: user.cpf_cnpj || '',
        officeAddress: user.office_address || '',
      }));
      setAddressData(parseAddress(user.office_address));
    } else {
      console.log('🔍 [PROFILE] No user data available');
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    const loadSubscription = async () => {
      try {
        setSubscriptionLoading(true);
        const currentSubscription = await subscriptionService.getMySubscription();
        if (isMounted) {
          setSubscription(currentSubscription);
        }
      } catch (error) {
        if (isMounted) {
          setSubscription(null);
        }
      } finally {
        if (isMounted) {
          setSubscriptionLoading(false);
        }
      }
    };

    loadSubscription();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Formatar CPF/CNPJ automaticamente
    if (name === 'cpf_cnpj') {
      const formatted = formatCPFOrCNPJ(value);
      setFormData({
        ...formData,
        [name]: formatted
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }

    // Validar força da senha quando o campo de nova senha é alterado
    if (name === 'newPassword') {
      const result = validatePassword(value);
      setPasswordErrors(result.errors);
      setPasswordWarnings(result.warnings);
      setPasswordScore(result.score);
    }
  };

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddressData((prev) => ({
      ...prev,
      [name]: name === 'cep' ? formatCep(value) : value,
    }));
  };

  const fetchAddressByCep = async (cepValue?: string) => {
    const cepDigits = (cepValue ?? addressData.cep).replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      return;
    }

    try {
      setCepLoading(true);
      const response = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
      const data = await response.json();

      if (data.erro) {
        handleOpenNotification('CEP não encontrado.', 'error');
        return;
      }

      setAddressData((prev) => ({
        ...prev,
        cep: formatCep(cepDigits),
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
      }));
    } catch (error) {
      handleOpenNotification('Erro ao buscar CEP. Tente novamente.', 'error');
    } finally {
      setCepLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      // Reset form to current user data when entering edit mode
      if (user) {
        const parsedAddress = parseAddress(user.office_address);
        setFormData({
          ...formData,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          oab: user.oab_number || '',
          estado_oab: user.estado_oab || '',
          cpf_cnpj: user.cpf_cnpj || '',
          officeAddress: user.office_address || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setAddressData(parsedAddress);
      }
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      logger.debug('Submitting data:', formData);
      // Validar CPF/CNPJ se fornecido
      if (formData.cpf_cnpj && !validateCPFOrCNPJ(formData.cpf_cnpj)) {
        setNotification({
          open: true,
          message: 'CPF ou CNPJ inválido.',
          severity: 'error'
        });
        return;
      }

      const addressValues = Object.values(addressData).map((value) => value.trim());
      const hasAnyAddressField = addressValues.some(Boolean);
      const requiredAddressFields = [
        addressData.cep,
        addressData.street,
        addressData.number,
        addressData.neighborhood,
        addressData.city,
        addressData.state,
      ].map((value) => value.trim());

      if (hasAnyAddressField && requiredAddressFields.some((value) => !value)) {
        setNotification({
          open: true,
          message: 'Preencha o endereço completo ou deixe todos os campos de endereço em branco.',
          severity: 'error'
        });
        return;
      }

      // Enviar os dados para a API - sem incluir o email que não é editável
      const updatedUser = await updateUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        oab: formData.oab,
        estado_oab: formData.estado_oab,
        cpf_cnpj: formData.cpf_cnpj,
        officeAddress: hasAnyAddressField ? serializeAddress(addressData) : '',
      });

      logger.debug('Response from API:', updatedUser);

      // Atualizar o formData com os dados retornados pela API, se disponível
      if (updatedUser) {
        // Não precisamos atualizar o formData aqui, pois o useEffect vai cuidar disso
        logger.debug('Form data after update:', formData);
      }

      setNotification({
        open: true,
        message: 'Perfil atualizado com sucesso!',
        severity: 'success'
      });
      setIsEditing(false);
    } catch (error) {
      logger.error('Error updating profile:', error);
      setNotification({
        open: true,
        message: 'Erro ao atualizar perfil. Tente novamente.',
        severity: 'error'
      });
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar se as senhas coincidem
    if (formData.newPassword !== formData.confirmPassword) {
      setNotification({
        open: true,
        message: 'As senhas não coincidem.',
        severity: 'error'
      });
      return;
    }

    // Validar força da senha
    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setNotification({
        open: true,
        message: 'A senha não atende aos requisitos mínimos de segurança.',
        severity: 'error'
      });
      return;
    }

    try {
      // Enviar requisição para atualizar a senha
      await updatePassword(formData.currentPassword, formData.newPassword);

      setNotification({
        open: true,
        message: 'Senha atualizada com sucesso!',
        severity: 'success'
      });

      // Limpar campos após atualização bem-sucedida
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Resetar estados de validação
      setPasswordErrors([]);
      setPasswordWarnings([]);
      setPasswordScore(0);
    } catch (error: any) {
      logger.error('Erro ao atualizar senha:', error);

      // Verificar se o erro é devido à senha atual incorreta
      if (error.response?.status === 401 ||
        error.response?.data?.detail?.includes('senha atual')) {
        setNotification({
          open: true,
          message: 'A senha atual está incorreta.',
          severity: 'error'
        });
      } else {
        setNotification({
          open: true,
          message: 'Erro ao atualizar senha. Tente novamente.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Helper function to open notifications
  const handleOpenNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };



  const handleAvatarClick = () => {
    setAvatarDialogOpen(true);
  };

  const handleCloseAvatarDialog = () => {
    setAvatarDialogOpen(false);
  };

  const handleAvatarChange = async () => {
    if (fileState.loading) return;

    try {
      await uploadFile(userService.uploadAvatar);

      // Update user context after successful upload
      const updatedUser = await userService.getCurrentUser();
      if (updateUser) {
        updateUser(updatedUser);
      }

      handleOpenNotification('Avatar atualizado com sucesso!', 'success');
      handleCloseAvatarDialog();
    } catch (error) {
      handleOpenNotification('Erro ao atualizar avatar. Tente novamente.', 'error');
    }
  };

  const handleAvatarButtonClick = () => {
    if (fileState.file) {
      handleAvatarChange();
    } else {
      triggerFileInput();
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h6">Carregando perfil...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <Tooltip title="Alterar foto">
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    width: 32,
                    height: 32,
                  }}
                  onClick={handleAvatarClick}
                >
                  <PhotoCamera fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            <Avatar
              sx={{ width: 80, height: 80, bgcolor: 'primary.main', cursor: 'pointer' }}
              src={user.avatar}
              onClick={handleAvatarClick}
            >
              {!user.avatar && `${user.firstName?.[0]}${user.lastName?.[0]}`}
            </Avatar>
          </Badge>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h5">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {user.email}
            </Typography>
            {user.oab_number && (
              <Typography variant="body2" color="text.secondary">
                OAB: {user.oab_number}
              </Typography>
            )}
          </Box>
        </Box>

        <Card sx={{ mb: 3, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Assinatura
                </Typography>
                <Typography variant="h5" color="primary" fontWeight={700}>
                  {subscription?.plan?.name || (user.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : 'Básico')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subscription
                    ? `Status: ${subscription.status === 'active' ? 'ativa' : subscription.status}`
                    : 'Você pode contratar um plano para desbloquear mais recursos e créditos.'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  color={subscription?.status === 'active' ? 'success' : 'default'}
                  label={subscriptionLoading ? 'Carregando plano...' : subscription?.status === 'active' ? 'Plano ativo' : 'Sem assinatura ativa'}
                  variant={subscription?.status === 'active' ? 'filled' : 'outlined'}
                />
                <Chip
                  color="primary"
                  label={`${user.available_credits || 0} créditos`}
                  variant="outlined"
                />
              </Box>
            </Box>

            <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowSubscriptionPopup(true)}
              >
                {subscription?.status === 'active' ? 'Ver planos e comprar créditos' : 'Ver planos'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/usage')}
              >
                Ver consumo
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="profile tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Dados Pessoais" />
          <Tab label="Segurança" />
          <Tab label="Notificações" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <form onSubmit={handleProfileSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sobrenome"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={true}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="CPF ou CNPJ"
                  name="cpf_cnpj"
                  value={formData.cpf_cnpj}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="CEP"
                  name="cep"
                  value={addressData.cep}
                  onChange={handleAddressInputChange}
                  onBlur={() => fetchAddressByCep()}
                  disabled={!isEditing || cepLoading}
                  placeholder="00000-000"
                  helperText="Ao informar o CEP, buscamos o endereço automaticamente."
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  label="Logradouro"
                  name="street"
                  value={addressData.street}
                  onChange={handleAddressInputChange}
                  disabled={!isEditing}
                  placeholder="Rua, avenida, alameda..."
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Número"
                  name="number"
                  value={addressData.number}
                  onChange={handleAddressInputChange}
                  disabled={!isEditing}
                  placeholder="123"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Complemento"
                  name="complement"
                  value={addressData.complement}
                  onChange={handleAddressInputChange}
                  disabled={!isEditing}
                  placeholder="Sala, conjunto, andar"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bairro"
                  name="neighborhood"
                  value={addressData.neighborhood}
                  onChange={handleAddressInputChange}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Cidade"
                  name="city"
                  value={addressData.city}
                  onChange={handleAddressInputChange}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="UF"
                  name="state"
                  value={addressData.state}
                  onChange={(e) =>
                    setAddressData((prev) => ({ ...prev, state: e.target.value.toUpperCase().slice(0, 2) }))
                  }
                  disabled={!isEditing}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Número OAB"
                  name="oab"
                  value={formData.oab}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="123456"
                />
              </Grid>
              {formData.oab && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Estado OAB"
                    name="estado_oab"
                    value={formData.estado_oab}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    SelectProps={{
                      native: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    <option value=""></option>
                    {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((estado) => (
                      <option key={estado} value={estado}>
                        {estado}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                {isEditing ? (
                  <>
                    <Button
                      onClick={handleEditToggle}
                      sx={{ mr: 2 }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Salvar Alterações
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleEditToggle}
                    variant="outlined"
                    color="primary"
                  >
                    Editar Perfil
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <form onSubmit={handlePasswordSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Senha Atual"
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nova Senha"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  required
                  error={passwordErrors.length > 0}
                />

                {formData.newPassword && (
                  <>
                    <PasswordStrengthMeter score={passwordScore} />

                    {passwordErrors.length > 0 && (
                      <List dense disablePadding sx={{ mt: 1 }}>
                        {passwordErrors.map((error, index) => (
                          <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <ErrorOutlineIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={error}
                              primaryTypographyProps={{
                                variant: 'caption',
                                color: 'error.main'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    {passwordWarnings.length > 0 && passwordErrors.length === 0 && (
                      <List dense disablePadding sx={{ mt: 1 }}>
                        {passwordWarnings.map((warning, index) => (
                          <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <WarningAmberIcon color="warning" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={warning}
                              primaryTypographyProps={{
                                variant: 'caption',
                                color: 'warning.main'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    {passwordErrors.length === 0 && passwordWarnings.length === 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CheckCircleOutlineIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="caption" color="success.main">
                          Senha atende a todos os requisitos de segurança
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirmar Nova Senha"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Atualizar Senha
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <EmailNotificationsSetting />
        </TabPanel>

      </Paper>

      {/* Popup Principal de Planos */}
      <ProfessionalSubscriptionPopup
        open={showSubscriptionPopup}
        onClose={() => setShowSubscriptionPopup(false)}
      />

      {/* Avatar Dialog */}
      <Dialog
        open={avatarDialogOpen}
        onClose={handleCloseAvatarDialog}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle>Alterar Avatar</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {fileState.preview ? (
              <Avatar
                src={fileState.preview}
                sx={{ width: 100, height: 100, mb: 2 }}
              />
            ) : (
              <Avatar
                src={user?.avatar || undefined}
                sx={{ width: 100, height: 100, mb: 2 }}
              >
                {!user.avatar && `${user.firstName?.[0]}${user.lastName?.[0]}`}
              </Avatar>
            )}

            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />

            {fileState.error && (
              <Typography color="error" variant="body2" sx={{ mt: 1, mb: 1 }}>
                {fileState.error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={triggerFileInput}
                disabled={fileState.loading}
              >
                Selecionar arquivo
              </Button>

              {fileState.file && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAvatarChange}
                  disabled={fileState.loading}
                >
                  {fileState.loading ? 'Enviando...' : 'Enviar'}
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAvatarDialog} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage; 